import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCuentas } from "../../../settings/hooks/useSettings";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { Label, Toast } from "../../../settings/components/SettingsUI";
import { overlayClose } from "../../../../shared/utils/overlayClose";

const MONEDAS       = ["CRC","USD","EUR"];
const EMPTY_CUENTA  = {banco:"",numero_cuenta:"",iban:"",moneda:"CRC",saldo:"0"};
const MONEDA_COLOR  = {CRC:"#f97316",USD:"#4ade80",EUR:"#60a5fa"};
const MONEDA_SYMBOL = {CRC:"₡",USD:"$",EUR:"€"};
const MONEDA_GRAD    = {
  CRC:"linear-gradient(135deg, rgba(249,115,22,0.16), rgba(249,115,22,0.02))",
  USD:"linear-gradient(135deg, rgba(74,222,128,0.16), rgba(74,222,128,0.02))",
  EUR:"linear-gradient(135deg, rgba(96,165,250,0.16), rgba(96,165,250,0.02))",
};

/* ── estilos compartidos, theme-aware ─────────────────────────────────────── */
const inputSt = {
  background:"var(--bg-input, var(--bg))", border:"1px solid var(--border-input, var(--border-card))",
  borderRadius:8, padding:"10px 13px", color:"var(--text-primary)",
  fontSize:"0.87rem", outline:"none", width:"100%", boxSizing:"border-box",
  transition:"border-color 0.15s",
};

function Btn2({ children, onClick, variant="primary", disabled, small, type }) {
  const [h,setH]=useState(false);
  const cfg = {
    primary: { bg: h?"#ea6b10":"#f97316", color:"#fff", border:"#f97316" },
    ghost:   { bg: h?"var(--hover-bg)":"transparent", color:"var(--text-muted)", border:"var(--border-card)" },
    danger:  { bg: h?"#dc2626":"#ef4444", color:"#fff", border:"#ef4444" },
  }[variant];
  return (
    <button type={type||"button"} onClick={onClick} disabled={disabled}
      onMouseOver={()=>setH(true)} onMouseOut={()=>setH(false)}
      style={{ background:cfg.bg, border:`1px solid ${cfg.border}`, borderRadius:9,
        color:cfg.color, padding:small?"7px 14px":"10px 20px",
        fontSize:small?"0.8rem":"0.86rem", fontWeight:400,
        cursor:disabled?"not-allowed":"pointer", opacity:disabled?0.5:1,
        transition:"all 0.15s", display:"inline-flex", alignItems:"center",
        gap:8, whiteSpace:"nowrap" }}>
      {children}
    </button>
  );
}

function IconCircleBtn({ icon, onClick, title, color="var(--text-muted)", hoverColor }) {
  const [h,setH]=useState(false);
  return (
    <button type="button" onClick={onClick} title={title}
      onMouseOver={()=>setH(true)} onMouseOut={()=>setH(false)}
      style={{ width:34, height:34, borderRadius:10, border:"1px solid var(--border-card)",
        background: h ? "var(--hover-bg)" : "transparent",
        color: h && hoverColor ? hoverColor : color, cursor:"pointer",
        display:"flex", alignItems:"center", justifyContent:"center",
        fontSize:"0.85rem", transition:"all 0.15s",
        borderColor: h && hoverColor ? hoverColor+"55" : "var(--border-card)" }}>
      <i className={`fa-solid ${icon}`}/>
    </button>
  );
}

function CardActionBtn({ icon, label, onClick, hoverColor="#f97316", last }) {
  const [h,setH]=useState(false);
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:6,
        background: h ? "var(--bg-card)" : "none", border:"none",
        borderRight: last ? "none" : "1px solid var(--border-card)",
        padding:"10px 4px", cursor:"pointer",
        color: h ? hoverColor : "var(--text-muted)", fontSize:"0.76rem", fontWeight:700,
        transition:"color 0.15s, background 0.15s" }}>
      <i className={`fa-solid ${icon}`} style={{ fontSize:"0.72rem" }}/>{label}
    </button>
  );
}

/* ═══════════════════════ Modal shell (overlay + panel) ═══════════════════════ */
function ModalShell({ onClose, icon, iconColor="#f97316", title, subtitle, width=460, children, footer }) {
  return (
    <>
      <div {...overlayClose(onClose)} style={{
        position:"fixed", inset:0, zIndex:1000, background:"rgba(0,0,0,0.66)", backdropFilter:"blur(3px)",
        display:"flex", alignItems:"center", justifyContent:"center", padding:16,
      }}>
        <div onClick={e=>e.stopPropagation()} style={{
          background:"var(--bg-card)", border:"1px solid var(--border-card)", borderRadius:16,
          width:`min(${width}px, 96vw)`, maxHeight:"90vh", overflow:"auto",
          boxShadow:"0 28px 70px rgba(0,0,0,0.5)",
        }}>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between",
            padding:"18px 22px", borderBottom:"1px solid var(--border-card)", gap:10 }}>
            <div style={{ display:"flex", gap:12, alignItems:"flex-start", minWidth:0 }}>
              <div style={{ width:40, height:40, borderRadius:11, flexShrink:0,
                background:`${iconColor}18`, border:`1px solid ${iconColor}30`,
                display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className={`fa-solid ${icon}`} style={{ color:iconColor, fontSize:"1rem" }}/>
              </div>
              <div style={{ minWidth:0 }}>
                <h2 style={{ margin:0, fontSize:"1.02rem", fontWeight:700, color:"var(--text-primary)" }}>{title}</h2>
                {subtitle && <p style={{ margin:"3px 0 0", color:"var(--text-muted)", fontSize:"0.79rem" }}>{subtitle}</p>}
              </div>
            </div>
            <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--text-muted)",
              cursor:"pointer", fontSize:"1.05rem", padding:4, flexShrink:0 }}>
              <i className="fa-solid fa-xmark"/>
            </button>
          </div>
          <div style={{ padding:22 }}>{children}</div>
          {footer && (
            <div style={{ display:"flex", justifyContent:"flex-end", gap:10,
              padding:"14px 22px", borderTop:"1px solid var(--border-card)" }}>
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function AlertBox({ children, onClose }) {
  return (
    <div style={{display:"flex",alignItems:"flex-start",gap:10,
      background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.28)",
      borderLeft:"3px solid #ef4444",borderRadius:8,padding:"10px 14px",marginBottom:14,
      color:"#f87171",fontSize:"0.83rem",lineHeight:1.55}}>
      <i className="fa-solid fa-triangle-exclamation" style={{color:"#f87171",flexShrink:0,marginTop:2}}/>
      <span style={{flex:1}}>{children}</span>
      {onClose && (
        <button type="button" onClick={onClose}
          style={{background:"none",border:"none",color:"#f87171",cursor:"pointer",padding:0,flexShrink:0,fontSize:"0.85rem"}}>
          <i className="fa-solid fa-xmark"/>
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════ Modal: Nueva / Editar cuenta ═══════════════════════ */
function CuentaFormModal({ cuenta, onClose, onSave, multimoneda }) {
  const editando = !!cuenta;
  const [form,setForm] = useState(cuenta
    ? {banco:cuenta.banco, numero_cuenta:cuenta.numero_cuenta, iban:cuenta.iban||"", moneda:cuenta.moneda, saldo:cuenta.saldo}
    : EMPTY_CUENTA);
  const [usdBlock,setUsdBlock] = useState(false);
  const [saving,setSaving] = useState(false);
  const [err,setErr] = useState("");

  async function handleSave() {
    if (!form.banco.trim() || !form.numero_cuenta.trim()) { setErr("Completá banco y número de cuenta."); return; }
    if (!multimoneda && form.moneda!=='CRC') { setUsdBlock(true); return; }
    setUsdBlock(false); setErr(""); setSaving(true);
    try { await onSave(form); } catch(e) { setErr(e.message); } finally { setSaving(false); }
  }

  return (
    <ModalShell onClose={onClose} icon={editando?"fa-pencil":"fa-plus"} iconColor="#f97316"
      title={editando?"Editar cuenta bancaria":"Nueva cuenta bancaria"}
      subtitle={editando?cuenta.banco:"Registrá una cuenta para conciliación, traslados y pagos"}
      footer={<>
        <Btn2 variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Btn2>
        <Btn2 onClick={handleSave} disabled={saving}>
          {saving ? <><i className="fa-solid fa-circle-notch fa-spin"/>Guardando…</> : <><i className="fa-solid fa-floppy-disk"/>{editando?"Guardar cambios":"Crear cuenta"}</>}
        </Btn2>
      </>}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:4}}>
        <div style={{gridColumn:"span 2"}}><Label required>Banco</Label>
          <input style={inputSt} placeholder="Ej: Banco Nacional" value={form.banco}
            onChange={e=>setForm(f=>({...f,banco:e.target.value}))}/></div>
        <div><Label required>N° Cuenta</Label>
          <input style={inputSt} placeholder="Número de cuenta" value={form.numero_cuenta}
            onChange={e=>setForm(f=>({...f,numero_cuenta:e.target.value}))}/></div>
        <div><Label>Moneda</Label>
          <select style={inputSt} value={form.moneda} onChange={e=>{setForm(f=>({...f,moneda:e.target.value}));if(e.target.value==='CRC')setUsdBlock(false);}}>
            {MONEDAS.map(m=><option key={m}>{m}</option>)}</select></div>
        <div style={{gridColumn:"span 2"}}><Label>IBAN</Label>
          <input style={inputSt} placeholder="Ej: CR21015201001026284066" value={form.iban}
            maxLength={34} onChange={e=>setForm(f=>({...f,iban:e.target.value.toUpperCase()}))}/></div>
        <div style={{gridColumn:"span 2"}}><Label>{editando?"Saldo":"Saldo inicial"}</Label>
          <input style={inputSt} type="number" value={form.saldo}
            onChange={e=>setForm(f=>({...f,saldo:e.target.value}))}/></div>
      </div>
      {usdBlock && (
        <AlertBox onClose={()=>setUsdBlock(false)}>
          Para usar moneda <strong>{form.moneda}</strong>, primero debés activar{" "}
          <strong>Soporte Multimoneda (USD)</strong> en{" "}
          <Link to="/settings" style={{color:"#f87171",fontWeight:700}}>Configuración</Link>.
        </AlertBox>
      )}
      {err && <AlertBox>{err}</AlertBox>}
    </ModalShell>
  );
}

/* ═══════════════════════ Modal: Eliminar cuenta ═══════════════════════ */
function EliminarCuentaModal({ cuenta, onClose, onConfirm }) {
  const [deleting,setDeleting] = useState(false);
  const [err,setErr] = useState("");
  return (
    <ModalShell onClose={onClose} icon="fa-trash" iconColor="#f87171"
      title="Eliminar cuenta bancaria" subtitle={cuenta.banco}
      footer={<>
        <Btn2 variant="ghost" onClick={onClose} disabled={deleting}>Cancelar</Btn2>
        <Btn2 variant="danger" disabled={deleting} onClick={async()=>{
          setDeleting(true); setErr("");
          try { await onConfirm(); } catch(e) { setErr(e.message); setDeleting(false); }
        }}>
          {deleting ? <><i className="fa-solid fa-circle-notch fa-spin"/>Eliminando…</> : <><i className="fa-solid fa-trash"/>Sí, eliminar</>}
        </Btn2>
      </>}>
      <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
        <i className="fa-solid fa-triangle-exclamation" style={{color:"#f87171",fontSize:"1.3rem",marginTop:2,flexShrink:0}}/>
        <div style={{fontSize:"0.87rem",color:"var(--text-primary)",lineHeight:1.6}}>
          ¿Seguro que querés eliminar la cuenta <strong>"{cuenta.banco}"</strong> ({cuenta.numero_cuenta})?
          <br/>
          <span style={{color:"var(--text-muted)",fontSize:"0.81rem"}}>
            Esta acción no se puede deshacer. Si tiene movimientos o recibos vinculados, la cuenta puede estar bloqueada.
          </span>
        </div>
      </div>
      {err && <div style={{marginTop:14}}><AlertBox>{err}</AlertBox></div>}
    </ModalShell>
  );
}

/* ═══════════════════════ Modal: Saldo inicial de conciliación ═══════════════════════ */
function SaldoInicialModal({ cuenta, onClose, showToast }) {
  const [loading,setLoading] = useState(true);
  const [existing,setExisting] = useState(null);
  const [form,setForm] = useState({fecha:"",monto:""});
  const [saving,setSaving] = useState(false);
  const sym = MONEDA_SYMBOL[cuenta.moneda] ?? cuenta.moneda;

  useEffect(()=>{
    (async ()=>{
      setLoading(true);
      try {
        const res = await apiFetch(`/api/finance/saldo-inicial-conciliacion/?cuenta_bancaria=${cuenta.id}`);
        const json = await res.json();
        if (json?.id) { setExisting(json); setForm({fecha:json.fecha_corte||"", monto:String(json.saldo_banco)}); }
      } catch {} finally { setLoading(false); }
    })();
  }, [cuenta.id]);

  async function guardar() {
    setSaving(true);
    try {
      const body = {cuenta_bancaria:cuenta.id, fecha:form.fecha, monto:form.monto};
      const res = existing
        ? await apiFetch(`/api/finance/saldo-inicial-conciliacion/${existing.id}/`, {method:"PATCH", body:JSON.stringify(body)})
        : await apiFetch(`/api/finance/saldo-inicial-conciliacion/`, {method:"POST", body:JSON.stringify(body)});
      const json = await res.json();
      if (!res.ok) throw new Error(json.error||"Error al guardar saldo inicial");
      showToast(json.detail||"Guardado");
      onClose();
    } catch(e) { showToast(e.message,"error"); } finally { setSaving(false); }
  }
  async function eliminar() {
    if (!existing) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/finance/saldo-inicial-conciliacion/${existing.id}/`, {method:"DELETE"});
      const json = await res.json();
      if (!res.ok) throw new Error(json.error||"Error al eliminar");
      showToast(json.detail||"Eliminado");
      onClose();
    } catch(e) { showToast(e.message,"error"); } finally { setSaving(false); }
  }

  return (
    <ModalShell onClose={onClose} icon="fa-scale-balanced" iconColor="#60a5fa"
      title="Saldo inicial de conciliación" subtitle={cuenta.banco}
      footer={loading ? null : <>
        <Btn2 variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Btn2>
        {existing && <Btn2 variant="danger" disabled={saving} onClick={eliminar}><i className="fa-solid fa-trash"/>Eliminar</Btn2>}
        <Btn2 disabled={saving} onClick={guardar}>
          {saving ? <><i className="fa-solid fa-circle-notch fa-spin"/>Guardando…</> : <><i className="fa-solid fa-floppy-disk"/>Guardar</>}
        </Btn2>
      </>}>
      {loading ? (
        <div style={{color:"var(--text-muted)",fontSize:"0.85rem",textAlign:"center",padding:20}}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{marginRight:8}}/>Cargando…
        </div>
      ) : (
        <>
          <p style={{margin:"0 0 16px",color:"var(--text-muted)",fontSize:"0.8rem",lineHeight:1.6}}>
            Saldo ya conciliado con el banco al migrar esta cuenta al sistema. Las conciliaciones
            posteriores parten de este monto en lugar de {sym}0.00.
          </p>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            <div><Label required>Fecha</Label>
              <input style={inputSt} type="date" value={form.fecha} onChange={e=>setForm(f=>({...f,fecha:e.target.value}))}/></div>
            <div><Label required>Monto ({sym})</Label>
              <input style={inputSt} type="number" value={form.monto} onChange={e=>setForm(f=>({...f,monto:e.target.value}))}/></div>
          </div>
        </>
      )}
    </ModalShell>
  );
}

/* ═══════════════════════ Tarjeta bancaria (visual, tipo card) ═══════════════════════ */
function BankCard({ cuenta, onEdit, onDelete, onConciliar, onSaldoInicial }) {
  const mc = MONEDA_COLOR[cuenta.moneda] ?? "#f97316";
  const [hover,setHover] = useState(false);
  return (
    <div
      onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{
        position:"relative", overflow:"hidden", borderRadius:18,
        background:"var(--bg-card)", border:"1px solid var(--border-card)",
        boxShadow: hover ? "0 14px 32px rgba(0,0,0,0.16)" : "0 1px 3px rgba(0,0,0,0.06)",
        transform: hover ? "translateY(-3px)" : "translateY(0)",
        transition:"transform 0.2s ease, box-shadow 0.2s ease",
      }}>
      {/* Fondo degradado por moneda */}
      <div style={{position:"absolute",inset:0,background:MONEDA_GRAD[cuenta.moneda]??MONEDA_GRAD.CRC,pointerEvents:"none"}}/>
      {/* Ícono decorativo grande, semi-transparente */}
      <i className="fa-solid fa-building-columns" style={{
        position:"absolute", right:-14, top:-10, fontSize:"5.6rem", color:mc, opacity:0.07, pointerEvents:"none",
      }}/>

      <div style={{position:"relative", padding:"20px 20px 16px"}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:18}}>
          <div style={{width:46,height:46,borderRadius:13,background:`${mc}20`,border:`1px solid ${mc}38`,
            display:"flex",alignItems:"center",justifyContent:"center"}}>
            <i className="fa-solid fa-building-columns" style={{color:mc,fontSize:"1.15rem"}}/>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <IconCircleBtn icon="fa-scale-balanced" title="Saldo inicial de conciliación" hoverColor="#60a5fa" onClick={onSaldoInicial}/>
            <span style={{padding:"3px 10px",borderRadius:20,background:`${mc}1f`,color:mc,fontSize:"0.68rem",fontWeight:800,letterSpacing:"0.4px"}}>
              {cuenta.moneda}
            </span>
          </div>
        </div>

        <div style={{fontWeight:800,color:"var(--text-primary)",fontSize:"1.04rem",marginBottom:3,
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
          {cuenta.banco}
        </div>
        <div style={{color:"var(--text-muted)",fontSize:"0.78rem",fontFamily:"monospace",display:"flex",alignItems:"center",gap:5}}>
          <i className="fa-solid fa-hashtag" style={{fontSize:"0.6rem",opacity:0.7}}/>{cuenta.numero_cuenta}
        </div>
        {cuenta.iban && (
          <div style={{color:"var(--text-dim, var(--text-muted))",fontSize:"0.71rem",fontFamily:"monospace",marginTop:3,
            display:"flex",alignItems:"center",gap:5,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            <i className="fa-solid fa-landmark" style={{fontSize:"0.58rem"}}/>{cuenta.iban}
          </div>
        )}

        <div style={{marginTop:18,paddingTop:14,borderTop:"1px dashed var(--border-card)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:3}}>
            <div style={{fontSize:"0.66rem",fontWeight:700,letterSpacing:"0.5px",textTransform:"uppercase",color:"var(--text-muted)"}}>
              Saldo actual
            </div>
            <span style={{padding:"2px 8px",borderRadius:20,background:"rgba(74,222,128,0.14)",
              border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",fontSize:"0.63rem",fontWeight:800,letterSpacing:"0.3px"}}>
              Activa
            </span>
          </div>
          <div style={{fontFamily:"monospace",fontWeight:800,fontSize:"1.32rem",color:"var(--text-primary)"}}>
            {MONEDA_SYMBOL[cuenta.moneda]??cuenta.moneda}{formatCurrency(Number(cuenta.saldo))}
          </div>
        </div>
      </div>

      {/* Barra de acciones */}
      <div style={{position:"relative",display:"flex",
        borderTop:"1px solid var(--border-card)",background:"var(--hover-bg)"}}>
        <CardActionBtn icon="fa-code-compare" label="Conciliar" hoverColor="#60a5fa" onClick={onConciliar}/>
        <CardActionBtn icon="fa-pencil" label="Editar" hoverColor="#f97316" onClick={onEdit}/>
        <CardActionBtn icon="fa-trash" label="Eliminar" hoverColor="#f87171" onClick={onDelete} last/>
      </div>
    </div>
  );
}

/* ═══════════════════════ Fila bancaria (vista lista) ═══════════════════════ */
function BankRow({ cuenta, onEdit, onDelete, onConciliar, onSaldoInicial }) {
  const mc = MONEDA_COLOR[cuenta.moneda] ?? "#f97316";
  return (
    <div style={{display:"flex",alignItems:"center",gap:14,
      background:"var(--bg-card)",border:"1px solid var(--border-card)",borderRadius:12,
      padding:"11px 14px",flexWrap:"wrap"}}>
      <div style={{width:38,height:38,borderRadius:10,flexShrink:0,background:`${mc}20`,border:`1px solid ${mc}38`,
        display:"flex",alignItems:"center",justifyContent:"center"}}>
        <i className="fa-solid fa-building-columns" style={{color:mc,fontSize:"0.95rem"}}/>
      </div>

      <div style={{flex:"2 1 160px",minWidth:120}}>
        <div style={{fontWeight:800,color:"var(--text-primary)",fontSize:"0.9rem",
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cuenta.banco}</div>
        <div style={{color:"var(--text-muted)",fontSize:"0.73rem",fontFamily:"monospace"}}>
          <i className="fa-solid fa-hashtag" style={{fontSize:"0.56rem",opacity:0.7,marginRight:3}}/>{cuenta.numero_cuenta}
        </div>
      </div>

      <span style={{padding:"3px 10px",borderRadius:20,background:`${mc}1f`,color:mc,fontSize:"0.66rem",fontWeight:800,letterSpacing:"0.4px",flexShrink:0}}>
        {cuenta.moneda}
      </span>

      <span style={{padding:"2px 8px",borderRadius:20,background:"rgba(74,222,128,0.14)",
        border:"1px solid rgba(74,222,128,0.3)",color:"#4ade80",fontSize:"0.62rem",fontWeight:800,letterSpacing:"0.3px",flexShrink:0}}>
        Activa
      </span>

      <div style={{flex:"1 1 130px",textAlign:"right",minWidth:110}}>
        <div style={{fontFamily:"monospace",fontWeight:800,fontSize:"0.95rem",color:"var(--text-primary)"}}>
          {MONEDA_SYMBOL[cuenta.moneda]??cuenta.moneda}{formatCurrency(Number(cuenta.saldo))}
        </div>
      </div>

      <div style={{display:"flex",gap:4,flexShrink:0}}>
        <IconCircleBtn icon="fa-scale-balanced" title="Saldo inicial de conciliación" hoverColor="#60a5fa" onClick={onSaldoInicial}/>
        <IconCircleBtn icon="fa-code-compare" title="Conciliar" hoverColor="#60a5fa" onClick={onConciliar}/>
        <IconCircleBtn icon="fa-pencil" title="Editar" hoverColor="#f97316" onClick={onEdit}/>
        <IconCircleBtn icon="fa-trash" title="Eliminar" hoverColor="#f87171" onClick={onDelete}/>
      </div>
    </div>
  );
}

/* ═══════════════════════════════ Página principal ═══════════════════════════════ */
export default function CuentasBancariasPage() {
  const { cuentas, loading, create, remove, update } = useCuentas();
  const multimoneda = useMultimoneda();
  const navigate = useNavigate();
  const [sinRelBancos, setSinRelBancos] = useState(false);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("grid"); // grid | list
  useEffect(()=>{
    apiFetch("/api/core/relaciones-catalogo/").then(r=>r.json()).then(d=>{
      setSinRelBancos(!d.relaciones?.rel_cuenta_bancos?.cuenta_actual);
    }).catch(()=>{});
  },[]);

  const [toast,setToast]=useState({msg:"",type:""});
  const showToast=(msg,type="success")=>{setToast({msg,type});setTimeout(()=>setToast({msg:"",type:""}),3500);};

  // Modales
  const [modalNueva, setModalNueva]           = useState(false);
  const [modalEditar, setModalEditar]         = useState(null); // cuenta | null
  const [modalEliminar, setModalEliminar]     = useState(null); // cuenta | null
  const [modalSaldoIni, setModalSaldoIni]     = useState(null); // cuenta | null

  const totalesPorMoneda = MONEDAS
    .map(m => ({ moneda:m, total: cuentas.filter(c=>c.moneda===m).reduce((s,c)=>s+Number(c.saldo||0),0), n: cuentas.filter(c=>c.moneda===m).length }))
    .filter(t => t.n > 0);

  const q = search.trim().toLowerCase();
  const cuentasFiltradas = !q ? cuentas : cuentas.filter(c =>
    (c.banco||"").toLowerCase().includes(q) ||
    (c.numero_cuenta||"").toLowerCase().includes(q) ||
    (c.iban||"").toLowerCase().includes(q)
  );

  const goConciliar = (cuenta) => navigate(`/finanzas/conciliacion?cuenta=${cuenta.id}`);

  return (
    <div>
      <span style={{color:"var(--text-muted)"}}>
        <Link to="/"><span style={{color:"var(--text-muted)"}}>Inicio </span></Link>/ <span style={{color:"var(--text-primary)"}}>Cuentas Bancarias</span>
      </span>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{marginBottom:6}}>
        <div>
          <h2 className="mt-2 me-2 p-0 title">Cuentas Bancarias</h2>
          <p className="mb-3" style={{color:"var(--text-muted)"}}>
            Cuentas bancarias registradas para conciliación, traslados y pagos.
          </p>
        </div>
        <Btn2 onClick={()=>{
          if (sinRelBancos) { showToast("Configurá primero la relación de Bancos en Relaciones Contables.","error"); return; }
          setModalNueva(true);
        }}>
          <i className="fa-solid fa-plus"/>Nueva cuenta
        </Btn2>
      </div>

      <Toast msg={toast.msg} type={toast.type}/>

      {sinRelBancos && (
        <div style={{display:"flex",alignItems:"flex-start",gap:10,
          background:"rgba(251,191,36,0.08)",border:"1px solid rgba(251,191,36,0.3)",
          borderRadius:10,padding:"12px 16px",marginBottom:16}}>
          <i className="fa-solid fa-triangle-exclamation" style={{color:"#fbbf24",marginTop:2,flexShrink:0}}/>
          <div>
            <p style={{margin:"0 0 3px",color:"#fbbf24",fontWeight:700,fontSize:"0.82rem"}}>
              Relación de Bancos sin configurar
            </p>
            <p style={{margin:0,color:"var(--text-muted)",fontSize:"0.76rem"}}>
              No podrás registrar cuentas bancarias hasta asignar la cuenta padre de{" "}
              <strong style={{color:"#fbbf24"}}>Bancos</strong> en{" "}
              <Link to="/settings?tab=relaciones" style={{color:"#f97316",fontWeight:700}}>Relaciones Contables</Link>.
            </p>
          </div>
        </div>
      )}

      {/* ── Resumen: saldo total por moneda ────────────────────────────── */}
      {totalesPorMoneda.length > 0 && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))",gap:12,marginBottom:22}}>
          {totalesPorMoneda.map(({moneda,total,n})=>{
            const mc = MONEDA_COLOR[moneda];
            return (
              <div key={moneda} style={{
                background:"var(--bg-card)", border:"1px solid var(--border-card)", borderRadius:14,
                padding:"16px 18px", position:"relative", overflow:"hidden",
                boxShadow:"0 1px 2px rgba(0,0,0,0.04)",
              }}>
                <div style={{position:"absolute",top:0,left:0,width:4,height:"100%",background:mc,opacity:0.9}}/>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
                  <div style={{minWidth:0}}>
                    <p style={{margin:0,fontSize:"0.67rem",fontWeight:700,letterSpacing:"0.5px",textTransform:"uppercase",color:"var(--text-muted)"}}>
                      Saldo total {moneda}
                    </p>
                    <p style={{margin:"6px 0 0",fontFamily:"monospace",fontWeight:800,fontSize:"1.25rem",color:"var(--text-primary)",lineHeight:1.1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {MONEDA_SYMBOL[moneda]}{formatCurrency(total)}
                    </p>
                    <p style={{margin:"6px 0 0",fontSize:"0.72rem",color:"var(--text-muted)"}}>
                      {n} {n===1?"cuenta":"cuentas"}
                    </p>
                  </div>
                  <div style={{width:42,height:42,borderRadius:12,flexShrink:0,background:`${mc}18`,border:`1px solid ${mc}30`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <i className="fa-solid fa-building-columns" style={{color:mc,fontSize:"1.05rem"}}/>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Mis cuentas: buscador + toggle vista ────────────────────────── */}
      {!loading && cuentas.length > 0 && (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap",marginBottom:14}}>
          <h3 style={{margin:0,fontSize:"0.95rem",fontWeight:700,color:"var(--text-primary)"}}>Mis cuentas</h3>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{position:"relative"}}>
              <i className="fa-solid fa-magnifying-glass" style={{position:"absolute",left:12,top:"50%",
                transform:"translateY(-50%)",color:"var(--text-muted)",fontSize:"0.78rem"}}/>
              <input
                value={search}
                onChange={e=>setSearch(e.target.value)}
                placeholder="Buscar cuenta…"
                style={{...inputSt, width:200, padding:"8px 12px 8px 32px", fontSize:"0.82rem"}}
              />
            </div>
            <div style={{display:"flex",background:"var(--bg-card)",border:"1px solid var(--border-card)",borderRadius:9,padding:3}}>
              <button type="button" onClick={()=>setView("grid")} title="Vista de tarjetas"
                style={{width:32,height:30,borderRadius:6,border:"none",cursor:"pointer",
                  background: view==="grid" ? "#f97316" : "transparent",
                  color: view==="grid" ? "#fff" : "var(--text-muted)",
                  display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}>
                <i className="fa-solid fa-table-cells-large" style={{fontSize:"0.78rem"}}/>
              </button>
              <button type="button" onClick={()=>setView("list")} title="Vista de lista"
                style={{width:32,height:30,borderRadius:6,border:"none",cursor:"pointer",
                  background: view==="list" ? "#f97316" : "transparent",
                  color: view==="list" ? "#fff" : "var(--text-muted)",
                  display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}>
                <i className="fa-solid fa-list" style={{fontSize:"0.78rem"}}/>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tarjetas / lista de cuentas bancarias ──────────────────────── */}
      {loading ? (
        <div style={{textAlign:"center",padding:48,color:"var(--text-secondary)"}}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{color:"#f97316",fontSize:"1.4rem",display:"block",marginBottom:10}}/>
          Cargando…
        </div>
      ) : cuentas.length===0 ? (
        <div style={{textAlign:"center",padding:56,background:"var(--bg-card)",border:"1px dashed var(--border-card)",borderRadius:16,color:"var(--text-muted)"}}>
          <i className="fa-solid fa-building-columns" style={{fontSize:"2.4rem",display:"block",marginBottom:14,opacity:0.3}}/>
          <p style={{margin:"0 0 14px",fontSize:"0.9rem"}}>No hay cuentas registradas.</p>
          <Btn2 onClick={()=>setModalNueva(true)}><i className="fa-solid fa-plus"/>Registrar la primera</Btn2>
        </div>
      ) : cuentasFiltradas.length===0 ? (
        <div style={{textAlign:"center",padding:40,background:"var(--bg-card)",border:"1px dashed var(--border-card)",borderRadius:16,color:"var(--text-muted)"}}>
          <i className="fa-solid fa-magnifying-glass" style={{fontSize:"1.6rem",display:"block",marginBottom:10,opacity:0.3}}/>
          <p style={{margin:0,fontSize:"0.85rem"}}>Ninguna cuenta coincide con "{search}".</p>
        </div>
      ) : view === "grid" ? (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(270px, 1fr))",gap:16}}>
          {cuentasFiltradas.map(c=>(
            <BankCard key={c.id} cuenta={c}
              onEdit={()=>setModalEditar(c)}
              onDelete={()=>setModalEliminar(c)}
              onConciliar={()=>goConciliar(c)}
              onSaldoInicial={()=>setModalSaldoIni(c)}
            />
          ))}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {cuentasFiltradas.map(c=>(
            <BankRow key={c.id} cuenta={c}
              onEdit={()=>setModalEditar(c)}
              onDelete={()=>setModalEliminar(c)}
              onConciliar={()=>goConciliar(c)}
              onSaldoInicial={()=>setModalSaldoIni(c)}
            />
          ))}
        </div>
      )}

      {/* ── Franja resumen: cuentas y totales por moneda ────────────────── */}
      {!loading && totalesPorMoneda.length > 0 && (
        <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",justifyContent:"center",gap:0,
          background:"var(--bg-card)",border:"1px solid var(--border-card)",borderRadius:14,
          padding:"14px 20px",marginTop:20}}>
          {totalesPorMoneda.flatMap(({moneda,total,n}, gi) => {
            const mc = MONEDA_COLOR[moneda];
            return [
              <div key={`n-${moneda}`} style={{display:"flex",alignItems:"center",gap:10,padding:"4px 20px",
                borderLeft: gi===0 ? "none" : "1px solid var(--border-card)"}}>
                <div style={{width:34,height:34,borderRadius:10,flexShrink:0,background:`${mc}18`,border:`1px solid ${mc}30`,
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <i className="fa-solid fa-building-columns" style={{color:mc,fontSize:"0.85rem"}}/>
                </div>
                <div>
                  <div style={{fontWeight:800,color:"var(--text-primary)",fontSize:"0.95rem",lineHeight:1.1}}>{n}</div>
                  <div style={{color:"var(--text-muted)",fontSize:"0.7rem"}}>{n===1?"Cuenta":"Cuentas"} en {moneda}</div>
                </div>
              </div>,
              <div key={`t-${moneda}`} style={{display:"flex",alignItems:"center",gap:10,padding:"4px 20px",
                borderLeft:"1px solid var(--border-card)"}}>
                <div style={{width:34,height:34,borderRadius:10,flexShrink:0,background:`${mc}18`,border:`1px solid ${mc}30`,
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <i className={`fa-solid ${moneda==="USD"?"fa-dollar-sign":"fa-coins"}`} style={{color:mc,fontSize:"0.85rem"}}/>
                </div>
                <div>
                  <div style={{fontWeight:800,color:"var(--text-primary)",fontSize:"0.95rem",lineHeight:1.1,fontFamily:"monospace"}}>
                    {MONEDA_SYMBOL[moneda]}{formatCurrency(total)}
                  </div>
                  <div style={{color:"var(--text-muted)",fontSize:"0.7rem"}}>Total en {moneda}</div>
                </div>
              </div>,
            ];
          })}
        </div>
      )}

      {/* ── Modales ──────────────────────────────────────────────────────── */}
      {modalNueva && (
        <CuentaFormModal multimoneda={multimoneda}
          onClose={()=>setModalNueva(false)}
          onSave={async(form)=>{
            await create(form);
            setModalNueva(false);
            showToast("Cuenta creada");
          }}
        />
      )}
      {modalEditar && (
        <CuentaFormModal cuenta={modalEditar} multimoneda={multimoneda}
          onClose={()=>setModalEditar(null)}
          onSave={async(form)=>{
            await update(modalEditar.id, form);
            setModalEditar(null);
            showToast("Cuenta actualizada");
          }}
        />
      )}
      {modalEliminar && (
        <EliminarCuentaModal cuenta={modalEliminar}
          onClose={()=>setModalEliminar(null)}
          onConfirm={async()=>{
            await remove(modalEliminar.id);
            setModalEliminar(null);
            showToast("Cuenta eliminada");
          }}
        />
      )}
      {modalSaldoIni && (
        <SaldoInicialModal cuenta={modalSaldoIni}
          onClose={()=>setModalSaldoIni(null)}
          showToast={showToast}
        />
      )}
    </div>
  );
}
